import type { ParcelDetails, RateQuote, SelectedContent, ShipmentPreferences } from "./types";

export function isLetterOnlyShipment(contents: SelectedContent[]): boolean {
  return (
    contents.length > 0 &&
    contents.every((content) => content.category === "DOCUMENTS_LETTERS" || content.category === "CARDS")
  );
}

export function filterQuotesForShipment(
  quotes: RateQuote[],
  contents: SelectedContent[],
  parcel: ParcelDetails,
  preferences: ShipmentPreferences | null,
): RateQuote[] {
  let filtered = isLetterOnlyShipment(contents) ? quotes.filter((quote) => isRealisticLetterQuote(quote, parcel)) : [...quotes];

  if (preferences?.priority === "COLLECTION_FROM_HOME") {
    filtered = filtered.filter((quote) => quote.fulfilment === "COLLECTION" || quote.fulfilment === "BOTH");
  } else if (preferences?.priority === "NEAREST_DROPOFF") {
    filtered = filtered.filter((quote) => quote.fulfilment === "DROPOFF" || quote.fulfilment === "BOTH");
  }

  if (preferences?.printerCapability === "NO") {
    filtered = filtered.filter((quote) => quote.printerlessAvailable);
  }

  return filtered;
}

export function isRealisticLetterQuote(quote: RateQuote, parcel: ParcelDetails): boolean {
  if (quote.isMock) return true;

  const carrierText = `${quote.carrier} ${quote.carrierDisplayName}`.toLowerCase();
  const serviceText = `${quote.service} ${quote.serviceDisplayName}`.toLowerCase();
  const isLetterProduct = /letter|letterbox|postable/.test(serviceText);

  // Royal Mail is the actual UK postal-letter network. Only retain its
  // explicit letter/large-letter products; a small-parcel product merely
  // accepting the dimensions does not make it appropriate for a letter.
  if (/royal[\s_-]*mail/.test(carrierText) || /royal[\s_-]*mail/.test(serviceText)) {
    return isLetterProduct;
  }

  // Evri advertises Postable as a large-letter product, but its published
  // minimum is 250g and 1cm deep. Parcel2Go can return it below those
  // minima, so enforce them here rather than displaying an unusable quote.
  if (/evri|hermes/.test(carrierText) && isLetterProduct) {
    return (
      parcel.weightGrams >= 250 &&
      parcel.weightGrams <= 1000 &&
      parcel.lengthCm <= 35 &&
      parcel.widthCm <= 23 &&
      parcel.heightCm >= 1 &&
      parcel.heightCm <= 3
    );
  }

  // Yodel, InPost, DPD, UPS and similar results are generic parcel/locker
  // products. A thin envelope may fit, but that is not a carrier-supported
  // postal letter product and should not outrank genuine letter services.
  return false;
}
